import React, { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Search, Plus, Filter } from 'lucide-react'
import { supabase } from '../lib/supabase'

const REGIONS = ['All', 'US', 'Europe', 'Southeast Asia', 'Canada', 'ANZ', 'South Asia', 'Middle East', 'East Asia', 'Africa']

export default function Clients() {
  const [clients, setClients] = useState([])
  const [search, setSearch] = useState('')
  const [region, setRegion] = useState('All')
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 30

  const fetchClients = useCallback(async () => {
    setLoading(true)
    let query = supabase
      .from('clients')
      .select('id, first_name, last_name, email, phone, country, city, region_bucket, contact_type', { count: 'exact' })
      .order('last_name', { ascending: true })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1)

    if (search) {
      query = query.or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,email.ilike.%${search}%,phone.ilike.%${search}%`)
    }
    if (region !== 'All') query = query.eq('region_bucket', region)

    const { data, count } = await query
    setClients(data || [])
    setTotal(count || 0)
    setLoading(false)
  }, [search, region, page])

  useEffect(() => {
    const timer = setTimeout(fetchClients, 300)
    return () => clearTimeout(timer)
  }, [fetchClients])

  function getInitials(c) {
    const f = (c.first_name || '').charAt(0)
    const l = (c.last_name || '').charAt(0)
    return (f + l).toUpperCase() || '?'
  }

  return (
    <div>
      <div className="page-header" style={{ justifyContent: 'space-between' }}>
        <h1 className="page-title">Clients <span style={{ fontSize: '14px', color: 'var(--gray-400)', fontWeight: '400' }}>({total.toLocaleString()})</span></h1>
        <Link to="/clients/new" className="btn btn-primary btn-sm">
          <Plus size={16} /> Add
        </Link>
      </div>

      {/* Search */}
      <div className="search-bar">
        <Search size={16} />
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0) }}
          placeholder="Search name, email or phone…"
        />
        {search && <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', color: 'var(--gray-400)', cursor: 'pointer', fontSize: '16px' }}>×</button>}
      </div>

      {/* Region filter */}
      <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', marginBottom: '12px', paddingBottom: '4px', scrollbarWidth: 'none' }}>
        {REGIONS.map(r => (
          <button key={r} onClick={() => { setRegion(r); setPage(0) }} style={{
            padding: '5px 12px', borderRadius: '20px', border: 'none', cursor: 'pointer',
            fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap',
            background: region === r ? 'var(--brown-dark)' : 'var(--white)',
            color: region === r ? 'var(--gold)' : 'var(--gray-600)',
            boxShadow: 'var(--shadow)', flexShrink: 0
          }}>{r}</button>
        ))}
      </div>

      {/* List */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div className="empty-state"><p style={{ color: 'var(--gray-400)' }}>Loading…</p></div>
        ) : clients.length === 0 ? (
          <div className="empty-state">
            <Search size={32} style={{ color: 'var(--gray-300)', marginBottom: '8px' }} />
            <p className="empty-state-title">No clients found</p>
            <p className="empty-state-sub">Try a different search term</p>
          </div>
        ) : (
          <>
            {clients.map(client => (
              <Link key={client.id} to={`/clients/${client.id}`} className="list-item">
                <div className="list-item-avatar">{getInitials(client)}</div>
                <div className="list-item-content">
                  <div className="list-item-title">
                    {[client.first_name, client.last_name].filter(Boolean).join(' ') || 'No name'}
                  </div>
                  <div className="list-item-sub">
                    {[client.email, client.city || client.country].filter(Boolean).join(' · ')}
                  </div>
                </div>
                <div className="list-item-right">
                  {client.contact_type && (
                    <span className={`badge ${client.contact_type === 'Past Client' ? 'badge-green' : 'badge-gray'}`} style={{ fontSize: '10px' }}>
                      {client.contact_type === 'Past Client' ? 'Client' : 'Lead'}
                    </span>
                  )}
                  {client.region_bucket && (
                    <span style={{ fontSize: '10px', color: 'var(--gray-400)' }}>{client.region_bucket}</span>
                  )}
                </div>
              </Link>
            ))}
            {/* Pagination */}
            {total > PAGE_SIZE && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid var(--gray-100)' }}>
                <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.max(0, p-1))} disabled={page === 0}>← Prev</button>
                <span style={{ fontSize: '13px', color: 'var(--gray-500)' }}>
                  {page * PAGE_SIZE + 1}–{Math.min((page+1)*PAGE_SIZE, total)} of {total.toLocaleString()}
                </span>
                <button className="btn btn-outline btn-sm" onClick={() => setPage(p => p+1)} disabled={(page+1)*PAGE_SIZE >= total}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
